/*
------------------------------------------------------------------------
API Wrapper with Session Recovery

Wraps fetch calls to automatically handle nonce expiration by:
1. Detecting 403 errors (expired nonce)
2. Attempting silent nonce refresh and retry
3. Falling back to page refresh if silent recovery fails
------------------------------------------------------------------------
*/

import { refreshNonce } from '@/utils/nonce.js';

/*
------------------------------------------------------------------------
Make an authenticated API request with automatic session recovery.

@param {string} url - The API endpoint URL.
@param {object} options - Fetch options (method, body, etc.).
@return {Promise<Response>} The fetch response.
------------------------------------------------------------------------
*/
export async function apiFetch(url, options = {}) {
    // Merge default headers with provided options
    const headers = {
        'Content-Type': 'application/json',
        'X-WP-Nonce': window.wpData?.nonce || '',
        ...options.headers,
    };

    const fetchOptions = {
        ...options,
        headers,
    };

    const response = await fetch(url, fetchOptions);

    // If we get a 403 (expired nonce), try silent recovery first
    if (response.status === 403) {
        $log.log('[api:apiFetch] 403 detected, attempting silent nonce refresh...');

        const newNonce = await refreshNonce();

        if (newNonce) {
            $log.log('[api:apiFetch] Nonce refreshed, retrying request...');

            // Retry with the fresh nonce
            const retryHeaders = {
                ...fetchOptions.headers,
                'X-WP-Nonce': newNonce,
            };

            const retryResponse = await fetch(url, {
                ...fetchOptions,
                headers: retryHeaders,
            });

            // If retry succeeds, return the response
            if (retryResponse.status !== 403) {
                $log.log('[api:apiFetch] Retry successful');
                return retryResponse;
            }

            $log.log('[api:apiFetch] Retry still returned 403');
        } else {
            $log.log('[api:apiFetch] Nonce refresh failed');
        }

        // Silent recovery failed - force page refresh as last resort
        $log.log('[api:apiFetch] Silent recovery failed, refreshing page...');
        window.location.reload();
        // Return a pending promise to prevent further execution while page reloads
        return new Promise(() => {});
    }

    return response;
}
